import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { walkInApi } from "../../api/walkIn";
import { QueryErrorBanner, TableSkeletonRows } from "../feedback/QueryFeedback";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { queryKeys } from "../../queryKeys";
import type { TierCountMap, WalkInGuest, WalkInGuestCreate } from "../../types/rsvp";
import { formatTierDistribution, sumTierCounts } from "../../types/rsvp";
import { formatINR } from "../../utils/format";
import "./WalkInPanel.css";

type TierOption = { id: string; name: string };

type Props = {
  eventId: string;
  token?: string | null;
  /** Tier id → display name */
  tierNames?: Record<string, string>;
  /** Ordered event tiers for CSV column mapping */
  tiers?: TierOption[];
  /** External search query (filters name / number) */
  search?: string;
};

export type WalkInPanelHandle = {
  openImport: () => void;
  isImporting: boolean;
};

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === "," && !inQuotes) {
      cells.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, " ");
}

function parseWalkInCsv(text: string, tiers: TierOption[]): WalkInGuestCreate[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  const header = splitCsvLine(lines[0]).map(normalizeHeader);
  const hasHeader =
    header.includes("name") ||
    header.includes("phone") ||
    header.includes("number") ||
    header.includes("guest number");
  const start = hasHeader ? 1 : 0;

  const nameIdx = hasHeader ? header.findIndex((h) => h === "name") : 0;
  const phoneIdx = hasHeader
    ? header.findIndex((h) => h === "phone" || h === "number" || h === "guest number")
    : 1;
  const partyIdx = hasHeader ? header.findIndex((h) => h.includes("party")) : 2;
  const amountIdx = hasHeader ? header.findIndex((h) => h.includes("amount")) : 3;

  const tierColById = new Map<string, number>();
  if (hasHeader) {
    tiers.forEach((tier) => {
      const tierKey = normalizeHeader(tier.name);
      const idx = header.findIndex(
        (h) => h === tierKey || h === normalizeHeader(tier.id) || h.includes(tierKey),
      );
      if (idx >= 0) tierColById.set(tier.id, idx);
    });
  }

  const rows: WalkInGuestCreate[] = [];
  for (let i = start; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]);
    const name = cells[nameIdx >= 0 ? nameIdx : 0] ?? "";
    const phone = cells[phoneIdx >= 0 ? phoneIdx : 1] ?? "";
    if (!name && !phone) continue;

    const partyRaw = partyIdx >= 0 ? cells[partyIdx] : cells[2];
    const amountRaw = amountIdx >= 0 ? cells[amountIdx] : cells[3];
    const party_size = Math.max(1, Number(partyRaw) || 1);
    const amount = amountRaw != null && amountRaw !== "" ? Number(amountRaw) : null;

    const tier_counts: TierCountMap = {};
    if (tierColById.size > 0) {
      tierColById.forEach((colIdx, tierId) => {
        const n = Math.max(0, Number(cells[colIdx]) || 0);
        if (n > 0) tier_counts[tierId] = n;
      });
    }

    if (Object.keys(tier_counts).length === 0 && tiers.length > 0) {
      tier_counts[tiers[0].id] = party_size;
    } else if (Object.keys(tier_counts).length === 0) {
      tier_counts.General = party_size;
    }

    rows.push({
      name,
      phone,
      party_size: sumTierCounts(tier_counts) || party_size,
      tier_counts,
      amount: Number.isFinite(amount as number) ? amount : null,
    });
  }
  return rows;
}

export const WalkInPanel = forwardRef<WalkInPanelHandle, Props>(function WalkInPanel(
  { eventId, token, tierNames = {}, tiers = [], search = "" },
  ref,
) {
  const queryClient = useQueryClient();
  const workspace = APP_WORKSPACE;
  const fileRef = useRef<HTMLInputElement | null>(null);

  const resolvedTiers = useMemo<TierOption[]>(() => {
    if (tiers.length > 0) return tiers;
    return Object.entries(tierNames).map(([id, name]) => ({ id, name }));
  }, [tiers, tierNames]);

  const guestsQuery = useQuery({
    queryKey: queryKeys.walkInGuests(workspace, eventId),
    queryFn: () => walkInApi.list(eventId, token ?? undefined),
    enabled: Boolean(eventId),
  });

  const guests = useMemo<WalkInGuest[]>(() => guestsQuery.data ?? [], [guestsQuery.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return guests;
    return guests.filter(
      (g) => g.name.toLowerCase().includes(q) || g.phone.toLowerCase().includes(q),
    );
  }, [guests, search]);

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text();
      const rows = parseWalkInCsv(text, resolvedTiers);
      if (rows.length === 0) throw new Error("No walk-in rows found in CSV.");
      return walkInApi.importCsv(eventId, rows, token ?? undefined);
    },
    onSuccess: async (result) => {
      toast.success(`Imported ${result.created} walk-in${result.created === 1 ? "" : "s"}`);
      if (result.errors.length) {
        toast.error(`${result.skipped} row(s) skipped`);
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.walkInGuests(workspace, eventId) });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Import failed"),
  });

  useImperativeHandle(
    ref,
    () => ({
      openImport: () => fileRef.current?.click(),
      isImporting: importMutation.isPending,
    }),
    [importMutation.isPending],
  );

  return (
    <div className="walk-in-panel">
      <div className="walk-in-panel__head">
        <div>
          <h2 className="app-shell-dash-panel-title">Walk-ins</h2>
          <p className="eventos-subtle">
            Guests who signed in via the Walk-in QR.
          </p>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) importMutation.mutate(file);
        }}
      />

      {guestsQuery.isError ? (
        <QueryErrorBanner
          message={guestsQuery.error instanceof Error ? guestsQuery.error.message : "Failed to load walk-ins"}
          onRetry={() => void guestsQuery.refetch()}
        />
      ) : null}

      <div className="walk-in-panel__table-wrap app-shell-table-wrap">
        <table className="app-shell-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Number</th>
              <th>Party Size</th>
              <th>Tier distribution</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {guestsQuery.isPending ? (
              <TableSkeletonRows rows={4} cols={5} />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="walk-in-panel__empty">
                  {search.trim()
                    ? "No walk-ins match this search."
                    : "No walk-ins yet. Share the Walk-in QR."}
                </td>
              </tr>
            ) : (
              filtered.map((guest) => (
                <tr key={guest.id}>
                  <td>{guest.name}</td>
                  <td className="eventos-table__num">{guest.phone}</td>
                  <td className="eventos-table__num">
                    {sumTierCounts(guest.tier_counts) || guest.party_size}
                  </td>
                  <td>{formatTierDistribution(guest.tier_counts, tierNames)}</td>
                  <td className="eventos-table__num">
                    {guest.amount != null ? formatINR(guest.amount) : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});
